import { type MutableRefObject, useEffect } from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler/src/ui/autobatch/index.tsx';
import { dragEnd } from '../../../../state/actions/drag/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../../state/index.tsx';
import { makeIsAnySelectedOrphanedSubtasks } from '../../../../state/selectors/selected-cards/index.tsx';
import { useSimplyMoveCards } from '../../../../state/state-hooks/use-simply-move-cards/index.tsx';

/**
 * Wire-up drag-and-drop into empty lists only
 */
export function useEmptyCardListDropTarget({
	elementRef,
	isCardListEmpty,
	cardListId,
}: {
	elementRef: MutableRefObject<HTMLElement | null>;
	isCardListEmpty: boolean;
	cardListId: string;
}) {
	const autobatch = useAutobatch();
	const simplyMoveCards = useSimplyMoveCards();
	const isAnySelectedOrphanedSubtask: boolean = useBacklogSelector(
		makeIsAnySelectedOrphanedSubtasks,
	);

	const dispatch = useBacklogDispatch();

	useEffect(() => {
		if (!elementRef.current) return undefined;

		return combine(
			dropTargetForElements({
				element: elementRef.current,
				// Only accept drop events on empty lists. This is because drop events will bubble from the cards
				// when the lists are non-empty. On that case, the card is itself handling move.
				canDrop: () => isCardListEmpty && !isAnySelectedOrphanedSubtask,
				onDrop: (args) => {
					autobatch(() => {
						const sourceCardId = Number(args.source.data.cardId);
						simplyMoveCards({
							source: {
								cardId: sourceCardId,
							},
							location: {
								type: 'list',
								targetListId: cardListId,
							},
						});

						dispatch(dragEnd());
					});
				},
			}),
		);
	}, [
		autobatch,
		cardListId,
		elementRef,
		isCardListEmpty,
		simplyMoveCards,
		isAnySelectedOrphanedSubtask,
		dispatch,
	]);
}
